// src/pages/CreateAgreementPage.js
import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useUserDoc } from '../hooks/useUserDoc';
import { createAgreement, uploadEvidence, getUserDoc, createVerificationToken } from '../firebase/services';
import { SvgIcon } from '../components/Icon';
import Navbar from '../components/Navbar';

import { getFunctions, httpsCallable } from 'firebase/functions';
import { FULL_ACCESS_MODE } from '../config/flags';

async function extractFromImage(base64, mimeType) {
  const functions = getFunctions();
  const extractFn = httpsCallable(functions, 'extractFromImage');
  const result = await extractFn({ base64, mimeType });
  return result.data.options || [];
}

const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result.split(',')[1]);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

export default function CreateAgreementPage() {
  const { user } = useAuth();
  const { isPremium, isBusiness, userDoc } = useUserDoc();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [partnerName, setPartnerName] = useState('');
  const [participants, setParticipants] = useState(['']);
  const [isGroup, setIsGroup] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showDate, setShowDate] = useState(false);
  const [deadline, setDeadline] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showVerify, setShowVerify] = useState(false);
  const [participantEmail, setParticipantEmail] = useState('');

  // AI state
  const [extracting, setExtracting] = useState(false);
  const [aiOptions, setAiOptions] = useState([]);
  const [showAiOptions, setShowAiOptions] = useState(false);
  const fileInputRef = useRef();

  const maxFiles = FULL_ACCESS_MODE ? 5 : isBusiness ? 999 : isPremium ? 5 : 1;

  const handleGroupToggle = () => {
    if (!FULL_ACCESS_MODE && !isPremium) { navigate('/pricing'); return; }
    if (!isGroup) setShowVerify(false);
    setIsGroup(!isGroup);
  };

  const handlePaymentToggle = () => {
    if (!FULL_ACCESS_MODE && !isPremium) { navigate('/pricing'); return; }
    setShowPayment(!showPayment);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!FULL_ACCESS_MODE && !isPremium) { navigate('/premium'); return; }
    setExtracting(true); setShowAiOptions(false); setError('');
    try {
      const base64 = await fileToBase64(file);
      const options = await extractFromImage(base64, file.type);
      if (options.length === 0) { setError(t('create.noAgreementsFound')); }
      else { setAiOptions(options); setShowAiOptions(true); }
    } catch (err) {
      console.error('AI extraction error:', err);
      setError(t('create.extractFailed'));
    } finally { setExtracting(false); }
  };

  const applyAiOption = (option) => {
    setTitle(option.title || '');
    setDetails(option.details || '');
    setPartnerName(option.partnerName || '');
    if (option.amount) { setAmount(String(option.amount)); setShowPayment(true); }
    setShowAiOptions(false);
  };

  const handleEvidenceUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!FULL_ACCESS_MODE && !isBusiness && evidenceFiles.length + files.length > maxFiles) {
      setError(`Max ${maxFiles} file${maxFiles > 1 ? 's' : ''} on your plan.`);
      return;
    }
    setEvidenceFiles(prev => [...prev, ...files]);
  };

  const removeEvidence = (idx) => {
    setEvidenceFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !details.trim()) { setError(t('create.fillRequired')); return; }
    if (!isGroup && !partnerName.trim()) { setError(t('create.addParticipantName')); return; }
    if (showVerify && (!participantEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(participantEmail.trim()))) {
      setError(t('create.invalidEmail')); return;
    }
    setLoading(true); setError('');

    try {
      const participantList = isGroup
        ? participants.filter(p => p.trim()).map(name => ({ name: name.trim(), status: 'Pending', confirmedAt: null }))
        : [];

      // Determine type from what's filled
      const hasPayment = showPayment && amount;
      let agreementType = 'agreement';
      if (hasPayment) agreementType = 'payment_agreement';
      if (isGroup) {
        const groupMap = { agreement: 'group_agreement', payment_agreement: 'group_payment_agreement' };
        agreementType = groupMap[agreementType] || 'group_agreement';
      }

      const isPaymentType = hasPayment;

      let creatorPaymentFields = {};
      if (isPaymentType) {
        const freshUserDoc = await getUserDoc(user.uid);
        creatorPaymentFields = {
          creatorZelleId: freshUserDoc?.zelleId || '',
          creatorVenmoUsername: freshUserDoc?.venmoUsername || '',
          creatorPaypalUsername: freshUserDoc?.paypalUsername || '',
          creatorMercadoPagoUsername: freshUserDoc?.mercadoPagoUsername || '',
          creatorTier: freshUserDoc?.tier || 'free',
        };
      }

      const { id: firestoreID, agreementID } = await createAgreement({
        type: agreementType,
        title: title.trim(),
        details: details.trim(),
        createdByName: user.displayName || user.email,
        createdByUID: user.uid,
        partnerName: partnerName.trim(),
        participants: participantList,
        participantCount: isGroup ? participantList.length + 1 : 2,
        deadline: (showDate && deadline) ? deadline : null,
        appointmentDate: null,
        amount: hasPayment ? Number(amount) : null,
        currency,
        isPremium,
        verified: showVerify || false,
        participantEmail: showVerify ? participantEmail.trim() : '',
        ...creatorPaymentFields,
      });

      if (evidenceFiles.length > 0) {
        const { addEvidenceToAgreement } = await import('../firebase/services');
        for (const file of evidenceFiles) {
          const ev = await uploadEvidence(file, agreementID);
          await addEvidenceToAgreement(firestoreID, ev);
        }
      }

      if (showVerify) {
        try {
          await createVerificationToken({
            agreementId: agreementID,
            email: participantEmail.trim(),
            deadline: (showDate && deadline) ? deadline : null,
          });
          const fns = getFunctions();
          const sendVerify = httpsCallable(fns, 'sendVerificationInvite');
          await sendVerify({ agreementId: agreementID, lang: i18n.language?.startsWith('es') ? 'es' : 'en' });
        } catch (verifyErr) {
          console.error('Verification email error:', verifyErr);
        }
      }

      navigate(`/pending/${agreementID}`);
    } catch (err) {
      setError(t('create.createFailed'));
      console.error(err);
    } finally { setLoading(false); }
  };

  const paymentMethodWarning = (method, field) => {
    if (userDoc?.[field]) return null;
    return <span style={{ fontSize: '0.7rem', color: 'var(--declined)' }}>{t('create.setupMethod', { method })}</span>;
  };

  return (
    <div className="page">
      <Navbar />
      <div className="container" style={{ paddingTop: 28, paddingBottom: 60 }}>
        <div className="page-title">{t('create.title')}</div>
        <div className="page-subtitle">{t('create.subtitle')}</div>

        {/* AI Panel */}
        <div className="ai-panel">
          <div className="ai-title"><SvgIcon name="star" size={14} color="var(--primary)" strokeWidth={2}/> {t('create.aiTitle')} {!FULL_ACCESS_MODE && !isPremium && <span style={{ fontSize: '0.7rem', color: 'var(--premium)' }}>{t('create.aiPremium')}</span>}</div>
          <div className="ai-subtitle">{t('create.aiSubtitle')}</div>
          <label className="btn btn-secondary" style={{ cursor: 'pointer', fontSize: '0.82rem' }}>
            {extracting ? t('create.extracting') : <><SvgIcon name="camera" size={14} color="var(--text)" strokeWidth={2}/> {t('create.uploadPhoto')}</>}
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} disabled={extracting} />
          </label>
        </div>

        {showAiOptions && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontWeight: 600, fontSize: '0.86rem', marginBottom: 10 }}>
              {t('create.foundAgreements', { count: aiOptions.length })}
            </p>
            {aiOptions.map((opt, i) => (
              <div key={i} className="ai-option" onClick={() => applyAiOption(opt)}>
                <div className="ai-option-title">{opt.title}</div>
                <div className="ai-option-detail">{opt.details}</div>
                {opt.partnerName && <div className="ai-option-partner">Partner: {opt.partnerName}</div>}
                {opt.amount > 0 && <div className="ai-option-amount">${opt.amount}</div>}
              </div>
            ))}
            <button className="btn btn-secondary btn-sm" onClick={() => setShowAiOptions(false)}>{t('create.fillManually')}</button>
          </div>
        )}

        <div className="card">
          <form onSubmit={handleSubmit}>
            {/* Title */}
            <div className="form-group">
              <label className="form-label">{t('create.agreementTitle')}</label>
              <input className="form-input" placeholder={t('create.titlePlaceholder')} value={title} onChange={e => setTitle(e.target.value)} required />
            </div>

            {/* Details */}
            <div className="form-group">
              <label className="form-label">{t('create.whatAgreed')}</label>
              <textarea className="form-input form-textarea" placeholder={t('create.detailsPlaceholder')} value={details} onChange={e => setDetails(e.target.value)} required />
            </div>

            {/* Participants */}
            {!isGroup ? (
              <div className="form-group">
                <label className="form-label">{t('create.otherParticipant')}</label>
                <input className="form-input" placeholder={t('create.theirName')} value={partnerName} onChange={e => setPartnerName(e.target.value)} required />
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">{t('create.participants')}</label>
                {participants.map((p, i) => (
                  <div key={i} className="participant-row">
                    <input className="form-input" placeholder={`Participant ${i + 1}`} value={p}
                      onChange={e => { const u = [...participants]; u[i] = e.target.value; setParticipants(u); }} />
                    {participants.length > 1 && (
                      <button type="button" style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: '1rem' }}
                        onClick={() => setParticipants(participants.filter((_, j) => j !== i))}>✕</button>
                    )}
                  </div>
                ))}
                <button type="button" className="add-participant-btn" onClick={() => setParticipants([...participants, ''])}>{t('create.addParticipant')}</button>
              </div>
            )}

            {/* Attachments */}
            <div className={`evidence-panel ${evidenceFiles.length > 0 ? 'has-files' : ''}`}>
              <div className="evidence-title">
                <SvgIcon name="paperclip" size={14} color="var(--text)" strokeWidth={2}/> {t('create.attachments')}
                {evidenceFiles.length > 0 && <span style={{ color: 'var(--confirmed)', fontSize: '0.74rem', fontWeight: 700 }}>● {t('create.filesAttached', { count: evidenceFiles.length })}</span>}
                <span style={{ fontSize: '0.7rem', color: 'var(--muted)', fontWeight: 400, marginLeft: 4 }}>{t('create.optional')}</span>
              </div>
              <div className="evidence-subtitle">{t('create.attachmentsSubtitle')}</div>
              <div className="evidence-grid">
                {evidenceFiles.map((f, i) => (
                  <div key={i} className="evidence-thumb">
                    <span>{f.type.includes('pdf') ? <SvgIcon name="doc" size={20} color="var(--muted)"/> : <SvgIcon name="camera" size={20} color="var(--muted)"/>}</span>
                    <div style={{ position: 'absolute', top: 3, right: 3, width: 16, height: 16, background: 'rgba(0,0,0,0.45)', borderRadius: '50%', color: 'white', fontSize: '0.55rem', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                      onClick={() => removeEvidence(i)}>✕</div>
                  </div>
                ))}
                {(FULL_ACCESS_MODE || isBusiness || evidenceFiles.length < maxFiles) && evidenceFiles.length < maxFiles && (
                  <label className="evidence-add" style={{ cursor: 'pointer' }}>
                    <span>+</span><span>{t('create.addFile')}</span>
                    <input type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={handleEvidenceUpload} multiple />
                  </label>
                )}
              </div>
            </div>

            {/* Toggle: Add participants */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 13, padding: '9px 12px', background: (FULL_ACCESS_MODE || isPremium) ? 'var(--primary-light)' : 'var(--premium-bg)', borderRadius: 8, border: `1px solid ${(FULL_ACCESS_MODE || isPremium) ? 'var(--secondary)' : 'var(--accent)'}` }}>
              <SvgIcon name="users" size={14} color="var(--primary)" strokeWidth={2}/>
              <span style={{ fontSize: '0.78rem', color: 'var(--primary)', fontWeight: 600 }}>
                {t('create.addParticipantsToggle')}
              </span>
              {!FULL_ACCESS_MODE && !isPremium && <SvgIcon name="lock" size={12} color="var(--premium)" strokeWidth={2}/>}
              <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto', marginBottom: 0, fontSize: '0.68rem', padding: '4px 10px' }} onClick={handleGroupToggle}>
                {isGroup ? t('create.switchIndividual') : (FULL_ACCESS_MODE || isPremium) ? t('create.makeGroup') : t('create.upgrade')}
              </button>
            </div>

            {/* Toggle: Add payment */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 13, padding: '9px 12px', background: showPayment ? 'var(--confirmed-bg)' : (FULL_ACCESS_MODE || isPremium) ? 'var(--primary-light)' : 'var(--premium-bg)', borderRadius: 8, border: `1px solid ${showPayment ? 'var(--confirmed)' : (FULL_ACCESS_MODE || isPremium) ? 'var(--secondary)' : 'var(--accent)'}` }}>
              <SvgIcon name="dollar" size={14} color={showPayment ? 'var(--confirmed)' : 'var(--primary)'} strokeWidth={2}/>
              <span style={{ fontSize: '0.78rem', color: showPayment ? 'var(--confirmed)' : 'var(--primary)', fontWeight: 600 }}>
                {t('create.addPaymentToggle')}
              </span>
              {!FULL_ACCESS_MODE && !isPremium && <SvgIcon name="lock" size={12} color="var(--premium)" strokeWidth={2}/>}
              <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto', marginBottom: 0, fontSize: '0.68rem', padding: '4px 10px' }} onClick={handlePaymentToggle}>
                {showPayment ? t('create.hidePayment') : (FULL_ACCESS_MODE || isPremium) ? t('create.addPayment') : t('create.upgrade')}
              </button>
            </div>

            {/* Payment section */}
            {showPayment && (
              <div style={{ padding: '12px', background: 'var(--bg)', borderRadius: 'var(--radius-sm)', marginBottom: 13, border: '1px solid var(--border)' }}>
                <div className="form-group">
                  <label className="form-label">{t('create.amount')}</label>
                  <div className="form-row">
                    <select className="form-select" style={{ width: 80, flex: 'none' }} value={currency} onChange={e => setCurrency(e.target.value)}>
                      <option>USD</option><option>EUR</option><option>GBP</option><option>CAD</option><option>MXN</option><option>ARS</option>
                    </select>
                    <input className="form-input" type="number" placeholder="0.00" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} required />
                  </div>
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginTop: 4 }}>
                  {t('create.paymentMethods')}: Venmo {paymentMethodWarning('Venmo', 'venmoUsername')} · Zelle {paymentMethodWarning('Zelle', 'zelleId')} · PayPal {paymentMethodWarning('PayPal', 'paypalUsername')} · Mercado Pago {paymentMethodWarning('Mercado Pago', 'mercadoPagoUsername')}
                  {userDoc?.stripeConnectAccountId && ' · Stripe'}
                </div>
              </div>
            )}

            {/* Toggle: Add date */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: showDate ? 8 : 13, padding: '9px 12px', background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <SvgIcon name="calendar" size={14} color="var(--text)" strokeWidth={2}/>
              <span style={{ fontSize: '0.78rem', color: 'var(--text)', fontWeight: 600 }}>
                {t('create.addDate')}
              </span>
              <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto', marginBottom: 0, fontSize: '0.68rem', padding: '4px 10px' }} onClick={() => {
                if (!showDate) {
                  const d = new Date();
                  d.setDate(d.getDate() + 7);
                  d.setHours(d.getHours() + 1, 0, 0, 0);
                  const pad = (n) => String(n).padStart(2, '0');
                  setDeadline(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
                  setShowDate(true);
                } else {
                  setDeadline('');
                  setShowDate(false);
                }
              }}>
                {showDate ? t('create.hideDate') : t('create.showDate')}
              </button>
            </div>

            {showDate && deadline && (
              <div style={{ padding: '12px', background: 'var(--bg)', borderRadius: 'var(--radius-sm)', marginBottom: 13, border: '1px solid var(--border)' }}>
                <div style={{ position: 'relative', width: '100%' }}>
                  <input className="form-input" type="datetime-local"
                    value={deadline}
                    onChange={e => setDeadline(e.target.value)}
                    style={{ width: '100%', paddingRight: 36 }}
                  />
                  <button type="button" onClick={() => { setDeadline(''); setShowDate(false); }} style={{
                    position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                    background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '50%',
                    width: 22, height: 22, cursor: 'pointer', fontSize: '0.78rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', padding: 0,
                  }}>✕</button>
                </div>
              </div>
            )}

            {/* Verified Agreement card (individual only) */}
            {!isGroup && (
              <div style={{
                padding: '14px 16px', marginBottom: 13, borderRadius: 10,
                border: `1.5px solid ${showVerify ? 'var(--confirmed)' : 'var(--border)'}`,
                background: showVerify ? 'var(--confirmed-bg)' : 'var(--surface)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <SvgIcon name="shield" size={16} color={showVerify ? 'var(--confirmed)' : 'var(--primary)'} strokeWidth={2}/>
                  <span style={{ fontSize: '0.86rem', fontWeight: 700, color: showVerify ? 'var(--confirmed)' : 'var(--primary)' }}>
                    {t('create.verifyToggle')}
                  </span>
                  <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto', marginBottom: 0, fontSize: '0.68rem', padding: '4px 10px' }} onClick={() => setShowVerify(!showVerify)}>
                    {showVerify ? t('create.removeVerify') : t('create.addVerify')}
                  </button>
                </div>
                <p style={{ fontSize: '0.74rem', color: 'var(--muted)', margin: 0, lineHeight: 1.4 }}>
                  {t('create.verifySubtitle')}
                </p>
                {showVerify && (
                  <div style={{ marginTop: 12 }}>
                    <label className="form-label">{t('create.verifyEmailLabel')}</label>
                    <input className="form-input" type="email" placeholder={t('create.verifyEmailPlaceholder')} value={participantEmail} onChange={e => setParticipantEmail(e.target.value)} required />
                  </div>
                )}
              </div>
            )}

            {error && <p className="error-msg">{error}</p>}

            <button className="btn btn-primary" type="submit" disabled={loading} style={{ marginTop: 6 }}>
              {loading ? t('create.creating') : t('create.createAndGetLink')}
            </button>
          </form>
        </div>
        <p style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--muted)' }}>
          {t('create.afterCreating')}
        </p>
      </div>
    </div>
  );
}
